import '@elisa-luukku-sites/cms-templates/index.content-blocks.css';
import * as richTextRenderer from '@contentful/rich-text-react-renderer';
import * as CL from '@design-system/component-library';
import Templates from '@elisa-luukku-sites/cms-templates';
import React, { useEffect, useState } from 'react';
import { Link, useLoaderData, useNavigate, useParams } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { IDEAT_ARTICLES_PER_PAGE, IDEAT_LANDING_ARTICLES_PER_PAGE } from '../../common/fetch.js';
import { useIdeatJsonLd } from '../../common/hooks/useIdeatJsonLd.js';
import { useIdeatMeta } from '../../common/hooks/useIdeatMeta.js';
import {
  allArticlesMsg,
  allTopicTagsMsg,
  archivesOfMsg,
  articlesInCategoryMsg,
  articlesWithKeywordMsg,
  ideatArticlesForCompaniesMsg,
  ideatMsg,
  ideatTopicTagsMsg,
  readArticleMsg,
  t,
} from '../../common/i18n/index.js';
import { AllArticlesIcon } from './AllArticlesIcon.js';
import type { IdeatCollectionMeta, IdeatLandingLoaderData } from '../../common/fetch.js';
import type { IdeatArticlesResponse } from '../../generated/api/ideatArticlesResponse.js';
import type { IdeatHomepageLink } from '../../generated/api/ideatHomepageLink.js';
import type { Document } from '@contentful/rich-text-types';

import './IdeatCollectionPage.scss';

const { ArticleGrid } = Templates.default;

const { documentToReactComponents } = richTextRenderer;

const RichText = ({ document: doc }: { document: Document }) => <>{documentToReactComponents(doc)}</>;

const ArticleLink = ({ href, ...props }: { href?: string } & React.ComponentProps<typeof Link>) => (
  <Link {...props} to={href ?? ''} />
);

const humanizeSlug = (slug: string) => slug.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());

const IMAGE_SIZES = [450, 600, 800, 1000, 1200, 1920];
const IMAGE_QUALITY = 75;

const getPictureSources = (src: string) =>
  IMAGE_SIZES.map(size => ({
    srcSet: `${src}?fm=webp&w=${size}&q=${IMAGE_QUALITY}`,
    media: `(max-width: ${size}px)`,
  }));

type IdeatCollectionBaseProps = IdeatArticlesResponse & {
  title: string;
  description: string;
  metaTitle?: string;
  metaDescription?: string;
  baseUrl: string;
  articlesPerPage: number;
};

const useHomeButtonCollapse = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  useEffect(() => {
    const handleButtonState = () => setIsCollapsed(window.scrollY > 300);
    handleButtonState();
    document.addEventListener('scroll', handleButtonState);
    return () => document.removeEventListener('scroll', handleButtonState);
  }, []);
  return isCollapsed;
};

const usePagination = (total: number, articlesPerPage: number, baseUrl: string) => {
  const navigate = useNavigate();
  const { pageNumber: pageParam } = useParams();
  const currentPage = Math.max(1, Number(pageParam ?? '1'));
  const totalPages = Math.max(1, Math.ceil(total / articlesPerPage));
  const pageUrl = `${baseUrl}/page/`;
  const gridProps =
    totalPages > 1
      ? {
          currentPage,
          totalPages,
          showAmount: 5,
          pageURL: pageUrl,
          onPageSelect: (page: number) => navigate(page <= 1 ? baseUrl : `${pageUrl}${page}`),
        }
      : undefined;
  return { gridProps, currentPage, totalPages };
};

const toGridArticles = (articles: IdeatArticlesResponse['articles']) =>
  articles.map(article => ({
    id: article.slug,
    title: article.title,
    excerpt: article.excerpt,
    date: article.publishedOn,
    imgSrc: article.featuredImageUrl,
    imgAlt: article.featuredImageAltText,
    link: `${paths.IDEAT}/${article.slug}`,
    category: article.category.title,
  }));

const HomeButton = ({ isCollapsed }: { isCollapsed: boolean }) => (
  <div className="buttonContainer">
    <Link className={`allArticlesButton${isCollapsed ? ' smallButton' : ''}`} to={paths.IDEAT}>
      <AllArticlesIcon />
      <span className="buttonText">{t.ZZWR(allArticlesMsg)}</span>
    </Link>
  </div>
);

const buildLandingLinks = (links: IdeatHomepageLink[], displayAllKeywordsLink?: boolean) => {
  const result = links.map(link => ({
    label: link.label,
    href:
      link.type === 'category'
        ? `${paths.IDEAT_CATEGORIES_BASE}/${link.slug}`
        : `${paths.IDEAT_TAGS_BASE}/${link.slug}`,
  }));
  if (displayAllKeywordsLink) {
    result.push({ label: t.ZZWS(allTopicTagsMsg), href: paths.IDEAT_KEYWORDS });
  }
  return result;
};

type HeroGradientColor = 'black' | 'blue' | 'blue-200' | 'blue-400' | 'blue-600' | 'blue-800';

type IdeatLandingContentProps = IdeatCollectionBaseProps & {
  landingLinks: IdeatHomepageLink[];
  displayAllKeywordsLink?: boolean;
  heroGradientColor?: HeroGradientColor;
  ogImage?: string;
  ogImageAlt?: string;
};

export const IdeatLandingContent = (props: IdeatLandingContentProps) => {
  const {
    title,
    description,
    metaTitle,
    metaDescription,
    baseUrl,
    total,
    articles,
    articlesPerPage,
    landingLinks,
    displayAllKeywordsLink,
    heroGradientColor,
    ogImage,
    ogImageAlt,
  } = props;
  const { gridProps, currentPage, totalPages } = usePagination(total, articlesPerPage, baseUrl);
  useIdeatMeta({
    title: metaTitle || title,
    description: metaDescription || description,
    image: ogImage,
    imageAlt: ogImageAlt,
    pagination: { currentPage, totalPages, baseUrl },
  });
  useIdeatJsonLd({ type: 'collection', title: metaTitle || title, description: metaDescription || description });
  const featuredArticle = articles[0];
  const gridArticles = articles.slice(1);

  return (
    <div className="page site cms-page of-ideat-page">
      <div className="ds-theme--default article-landing-hero">
        <CL.Hero
          className="hero"
          gradientColor={heroGradientColor || 'black'}
          mobileContentPosition="below"
          bgImage={
            featuredArticle && (
              <CL.Picture
                loadingMethod="eager"
                i18n_picture_altText={featuredArticle.featuredImageAltText}
                src={featuredArticle.featuredImageUrl}
                sources={getPictureSources(featuredArticle.featuredImageUrl)}
                fit="cover"
                position="center center"
              />
            )
          }
        >
          {featuredArticle && (
            <div className="heroContentContainer">
              <h1>{featuredArticle.title}</h1>
              <p className="ds-text--l">{featuredArticle.excerpt}</p>
              <Link
                className="ds-link ds-link--style-link-button ds-link--button-color-primary ds-link--button-size-m"
                to={`${paths.IDEAT}/${featuredArticle.slug}`}
              >
                {t.ZZWT(readArticleMsg)}
              </Link>
            </div>
          )}
        </CL.Hero>
      </div>
      <main className="content">
        <nav className="LinkList" aria-label={t.ZZWY(ideatTopicTagsMsg)}>
          {buildLandingLinks(landingLinks, displayAllKeywordsLink).map(link => (
            <Link key={link.label} className="LinkListItem" to={link.href}>
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="gridContainer">
          <ArticleGrid
            linkComponent={ArticleLink}
            articles={toGridArticles(gridArticles)}
            paginationProps={gridProps}
          />
        </div>
      </main>
    </div>
  );
};

type IdeatFilterContentProps = IdeatCollectionBaseProps & {
  pageIntro?: Document;
};

export const IdeatFilterContent = (props: IdeatFilterContentProps) => {
  const { title, description, metaTitle, metaDescription, pageIntro, baseUrl, total, articles, articlesPerPage } =
    props;
  const { gridProps, currentPage, totalPages } = usePagination(total, articlesPerPage, baseUrl);
  useIdeatMeta({
    title: metaTitle || title,
    description: metaDescription || description,
    pagination: { currentPage, totalPages, baseUrl },
  });
  useIdeatJsonLd({ type: 'collection', title: metaTitle || title, description: metaDescription || description });
  const isHomeButtonCollapsed = useHomeButtonCollapse();

  return (
    <div className="page site cms-page of-ideat-page">
      <HomeButton isCollapsed={isHomeButtonCollapsed} />
      <div className="categoriesContainer">
        <div className="titleContainer">
          <h1 className="title">{title}</h1>
          {pageIntro && (
            <div className="ingressText ds-text--l">
              <RichText document={pageIntro} />
            </div>
          )}
        </div>
        <div className="collectionGrid">
          <ArticleGrid linkComponent={ArticleLink} articles={toGridArticles(articles)} paginationProps={gridProps} />
        </div>
      </div>
    </div>
  );
};

export const IdeatLandingPath = () => {
  const { articles, homepageConfig } = useLoaderData() as IdeatLandingLoaderData;
  return (
    <IdeatLandingContent
      {...articles}
      title={t.ZZWW(ideatMsg)}
      description={t.ZZWX(ideatArticlesForCompaniesMsg)}
      metaTitle={homepageConfig?.metaTitle}
      metaDescription={homepageConfig?.metaDescription}
      baseUrl={paths.IDEAT}
      articlesPerPage={IDEAT_LANDING_ARTICLES_PER_PAGE}
      landingLinks={homepageConfig?.links}
      displayAllKeywordsLink={homepageConfig?.displayAllKeywordsLink}
      heroGradientColor={homepageConfig?.heroGradientColor}
      ogImage={homepageConfig?.openGraphImageUrl}
      ogImageAlt={homepageConfig?.openGraphImageAltText}
    />
  );
};

const IdeatCollectionPath = ({
  paramKey,
  baseUrl,
  fallbackDescription,
}: {
  paramKey: string;
  baseUrl: string;
  fallbackDescription: (name: string) => string;
}) => {
  const { articles, meta } = useLoaderData() as {
    articles: IdeatArticlesResponse;
    meta: IdeatCollectionMeta;
  };
  const params = useParams();
  const slug = params[paramKey] || '';
  const name = meta.title || humanizeSlug(slug);
  const archiveTitle = t.ZZX1(archivesOfMsg, name);
  return (
    <IdeatFilterContent
      {...articles}
      title={name}
      description={meta.description || fallbackDescription(name)}
      metaTitle={meta.metaTitle || archiveTitle}
      metaDescription={meta.metaDescription}
      pageIntro={meta.pageIntro as Document | undefined}
      baseUrl={`${baseUrl}/${slug}`}
      articlesPerPage={IDEAT_ARTICLES_PER_PAGE}
    />
  );
};

export const IdeatCategoryPath = () => (
  <IdeatCollectionPath
    paramKey="ideatCategorySlug"
    baseUrl={paths.IDEAT_CATEGORIES_BASE}
    fallbackDescription={name => t.ZZWZ(articlesInCategoryMsg, name)}
  />
);

export const IdeatTagPath = () => (
  <IdeatCollectionPath
    paramKey="ideatTagSlug"
    baseUrl={paths.IDEAT_TAGS_BASE}
    fallbackDescription={name => t.ZZX0(articlesWithKeywordMsg, name)}
  />
);
